/**
 * Публічна реєстрація нового tenant'у + першого OWNER користувача.
 *
 * Після успіху — auto-login (отримані токени зберігаються) + редірект на /.
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signup as apiSignup } from '@/api/auth';
import { useAuth } from '@/auth/store';

export default function SignupPage() {
  const navigate = useNavigate();
  const setTokens = useAuth((s) => s.setTokens);
  const setActiveTenant = useAuth((s) => s.setActiveTenant);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [fullName, setFullName] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Пароль має містити мінімум 8 символів');
      return;
    }
    if (password !== passwordConfirm) {
      setError('Паролі не співпадають');
      return;
    }

    setLoading(true);
    try {
      const tok = await apiSignup({
        email: email.trim(),
        password,
        full_name: fullName.trim(),
        tenant_name: tenantName.trim(),
      });
      setTokens(tok.access_token, tok.refresh_token);
      // BUG-FIX: scrub застарілий activeTenantId з попередньої сесії
      // (інакше axios відправить старий X-Tenant-Id, backend 403 →
      // 403-handler робить full reload на /select-tenant?reason=membership_lost
      // і користувач застрягає на цьому URL).
      setActiveTenant(null);
      // Порція 3.0.5.1b: signup створює tenant одразу, але redirect через
      // /select-tenant — SelectTenantPage побачить 1 tenant і автоматично
      // активує його. Так уніфіковано з login flow.
      navigate('/select-tenant', { replace: true });
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number; data?: { detail?: string } } })
        ?.response;
      if (status?.status === 409) {
        setError('Email уже зареєстровано — увійдіть або скиньте пароль');
      } else if (status?.status === 422) {
        setError('Перевірте формат email і довжину пароля (мін. 8 символів)');
      } else {
        setError(status?.data?.detail ?? 'Помилка реєстрації');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login">
      <form onSubmit={onSubmit}>
        <h1>Створення акаунта</h1>
        <p style={{ fontSize: '0.85rem', color: 'var(--muted)', marginBottom: '1rem' }}>
          Створюємо обліковий запис вашого бізнесу. Ви станете власником
          (OWNER) і зможете додавати співробітників пізніше.
        </p>

        <div className="field">
          <label>Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            placeholder="you@company.com"
          />
        </div>

        <div className="field">
          <label>Ваше ПІБ</label>
          <input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            placeholder="Іванов Іван Іванович"
          />
        </div>

        <div className="field">
          <label>Назва акаунта</label>
          <input
            value={tenantName}
            onChange={(e) => setTenantName(e.target.value)}
            required
            placeholder='ТОВ "Альфа" / ФОП Іваненко / Моя компанія'
          />
          <small style={{ color: 'var(--muted)' }}>
            Можна змінити пізніше у налаштуваннях.
          </small>
        </div>

        <div className="field">
          <label>Пароль (мін. 8 символів)</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
          />
        </div>

        <div className="field">
          <label>Повторіть пароль</label>
          <input
            type="password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            required
          />
        </div>

        <button
          type="submit"
          className="primary"
          style={{ width: '100%' }}
          disabled={loading}
        >
          {loading ? 'Створюємо акаунт…' : 'Зареєструватися'}
        </button>

        {error && <div className="error">{error}</div>}

        <p style={{ marginTop: '1rem', fontSize: '0.85rem', textAlign: 'center' }}>
          Уже маєте акаунт? <Link to="/login">Увійти</Link>
        </p>
      </form>
    </div>
  );
}
